<template>
    <div class="w-full">
        <div class="inline">
            <page-headline>{{$t('pages.auctions.planning.overview.title')}}</page-headline>
        </div>
        <div class="w-full gap-2 grid grid-cols-1 xs:grid-cols-2 xl:grid-cols-4">
            <div class="w-full">
                <div class="bg-white tile h-full">
                    <div class="px-4 py-5 sm:p-6">
                        <h3 class="text-base leading-6 font-medium">{{$t('pages.auctions.planning.overview.title_1')}}</h3>
                        <div class="mt-2 max-w-xl text-sm text-gray-500">
                            <p>{{$t('pages.auctions.planning.overview.teaser_1')}}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="bg-white tile h-full">
                    <div class="px-4 py-5 sm:p-6 h-full">
                        <div class="flex items-stretch flex-col h-full">
                            <div class="self-stretch mb-5">
                                <h3 class="text-base leading-6 font-medium">{{$t('pages.auctions.planning.overview.title_2')}}</h3>
                                <div class="mt-2 max-w-xl text-sm text-gray-500">
                                    <p>{{$t('pages.auctions.planning.overview.teaser_2')}}</p>
                                </div>
                            </div>
                            <div class="mr-auto mt-auto">
                                <button @click="goToUpload()" type="button" class="btn btn-primary">
                                    {{$t('pages.auctions.planning.overview.upload_button_label')}}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="bg-white tile h-full">
                    <div class="px-4 py-5 sm:p-6 h-full">
                        <div class="flex items-stretch flex-col h-full">
                            <div class="self-stretch mb-5">
                                <h3 class="text-base leading-6 font-medium">{{$t('pages.auctions.planning.overview.title_3')}}</h3>
                                <div class="mt-2 max-w-xl text-sm text-gray-500">
                                    <p v-html="$t('pages.auctions.planning.overview.teaser_3')"></p>
                                </div>
                            </div>
                            <div class="mr-auto mt-auto">
                                <a :href="route('auctions.planning.download.currentData', {})">
                                    <button type="button" class="btn btn-primary">
                                        {{$t('pages.auctions.planning.overview.download_button_label')}}
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="w-full">
                <div class="bg-white tile h-full">
                    <div class="px-4 py-5 sm:p-6 h-full">
                        <div class="flex items-stretch flex-col h-full">
                            <div class="self-stretch mb-5">
                                <h3 class="text-base leading-6 font-medium">{{$t('pages.auctions.planning.overview.title_4')}}</h3>
                                <div class="mt-2 max-w-xl text-sm text-gray-500">
                                    <p v-html="$t('pages.auctions.planning.overview.teaser_4')"></p>
                                </div>
                            </div>
                            <div class="mr-auto mt-auto">
                                <button type="button" class="btn btn-danger" @click="showDeleteDialog()">
                                    {{$t('pages.auctions.planning.overview.delete_all_data.delete_button')}}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="w-full flex flex-wrap pt-6">
            <div class="mt-8 flex flex-col w-full">
                <div
                    class="w-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                    v-if="auctions.total > 0"
                >
                    <table class="table-default">
                        <thead>
                            <tr>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.date_start')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.name')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.product_id')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.shop_price' + this.$page.props.tenant.labels_key)}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.max_price')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.total_amount')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.level_count')}}</th>
                                <th scope="col">{{$t('pages.auctions.planning.overview.auctions_columns.last_edit')}}</th>
                                <th scope="col">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white">
                            <tr
                                v-if="auctions.total > 0"
                                v-for="auction in auctions.data" :key="auction.id"
                                @click="goToDetails(auction.id)"
                                class="cursor-pointer hover:bg-gray-100"
                                :class="{'bg-gray-100' : activeAuctionId === auction.id}"
                            >
                                <td>{{ auction.date_start | formatDay }}</td>
                                <td>
                                    <shorten :value="auction.name" :tooltip-class="'tooltip-auction-name'"/>
                                </td>
                                <td>{{ auction.article_id }}</td>
                                <td>{{ auction.attributes?.shop_price | toCurrency }}</td>
                                <td>{{ Math.max(...auction.levels.map(l => l.price)) | toCurrency }}</td>
                                <td>{{ auction.article_quantity }}</td>
                                <td>{{ auction.levels && auction.levels.length > 0 ? auction.levels.length : '' }}</td>
                                <td>
                                    <div class="group relative inline-block" :class="{'auction-source-bold' : getRawAuction(auction)?.source === 2}">
                                            <span class="">
                                                {{$t('shared.auction_source.sources.' + rawAuctionSource(auction)) }}
                                            </span>
                                        <div class="tooltip-tl">
                                            {{getRawAuction(auction)?.updated_by?.name }} {{getRawAuction(auction)?.updated_by?.lastname}} <br>
                                            {{$t('shared.auction_source.sources.' + (getRawAuction(auction)?.source || 1)) }} |
                                            {{ getRawAuction(auction)?.updated_at | formatDateAndTimeAmPm }}
                                        </div>
                                    </div>
                                </td>
                                <td @click.stop>
                                    <button @click="goToDelete(auction.id)" class="bg-white py-1 px-2 rounded text-red-500 hover:bg-red-500 hover:text-white">X</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="bg-white tile my-5" v-if="auctions.total === 0">
                    <div class="px-4 py-5 sm:p-6">
                        <div class="flex sm:items-start sm:justify-between">
                            <div class="flex">
                                <h3 class="text-xl leading-6 font-medium mr-5"><icon name="emoji-sad" class="h-10"/></h3>
                                <div class="mt-2 max-w-xl text-sm text-gray-500">
                                    <p>{{$t('pages.auctions.planning.overview.no_auctions_planned')}}</p>
                                </div>
                            </div>
                            <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                                <button @click="goToUpload()" type="button" class="btn btn-primary">
                                    {{$t('pages.auctions.planning.overview.upload_button_label')}}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <modal ref="deletePlanningData"
               :show-cancel="false"
               :show-confirm="false"
               @cancel="cancelDeleteAuctionsDialog"
               :title="$t('pages.auctions.planning.overview.delete_all_data.title')">
            <div class="text-sm">
                <p class="mb-4">{{$t('pages.auctions.planning.overview.delete_all_data.text_1')}}</p>
                <p class="mb-4">{{$t('pages.auctions.planning.overview.delete_all_data.text_2')}}</p>
                <p class="mb-5">{{$t('pages.auctions.planning.overview.delete_all_data.text_3')}}</p>
                <div class="loading-indicator" v-if="sendingDeletePlanningData">
                    <icon name="clock" class="w-8 h-8 mx-auto animate-spin"/>
                </div>
            </div>
            <alert-message
                v-if="showDeletePlanningDataFailed && deletePlanningDataErrors && deletePlanningDataErrors.length > 0"
                :errorType="ERROR_TYPE_ERROR"
                :show-as-list="false"
                :messages="deletePlanningDataErrors"
                :display-errors-title="false"
                @close="deletePlanningDataFailedAlertClosed()"
                class="sm:px-0 lg:px-0 pr-4 sm:pr-6 lg:pr-4"
            />
            <div class="mt-5 pt-4 flex justify-between">
                <button type="button" class="btn btn-secondary" @click="cancelDeleteAuctionsDialog">{{$t('shared.modals.cancel')}}</button>
                <a :href="route('auctions.planning.download.currentData', {})">
                    <button type="button" class="btn btn-primary">
                        {{$t('pages.auctions.planning.overview.delete_all_data.download_button')}}
                    </button>
                </a>
                <button type="button" class="btn btn-danger" @click="showConfirmDeletePlanningData">{{$t('pages.auctions.planning.overview.delete_all_data.confirm_and_delete_button')}}</button>
            </div>
        </modal>

        <modal ref="confirmDeletePlanningData"
               @confirm="confirmDelete"
               @cancel="cancelConfirmDeleteAuctionsDialog"
               :dangerous="true"
               :title="$t('pages.auctions.planning.overview.delete_all_data.confirm_deletion.title')">
            <p class="">{{$t('pages.auctions.planning.overview.delete_all_data.confirm_deletion.text')}}</p>
        </modal>
    </div>
</template>

<script>
import AuctionState from "@/mixins/AuctionState";
import PageHeadline from "@/Shared/PageHeadline";
import Icon from "@/Shared/Icon";
import {ErrorTypes} from '../../mixins/ErrorTypes'
import Shorten from "../../Shared/Shorten.vue";
import Modal from "@/Shared/Modals/Modal";
import AlertMessage from "@/Shared/Alerts/AlertMessage";

export default {
    mixins: [AuctionState, ErrorTypes],
    components: {
        PageHeadline,
        Icon,
        Shorten,
        Modal,
        AlertMessage
    },
    methods: {
        goToDetails(auctionId) {
            this.$emit('go-to-details', auctionId);
        },
        goToDelete(auctionId) {
            this.$emit('go-to-delete', auctionId);
        },
        goToUpload() {
            this.$emit('go-to-upload');
        },
        refreshAuctionsList() {
            this.$emit('reload-auctions');
        },
        confirmDelete() {
            if(this.sendingDeletePlanningData === true) {
                return;
            }
            this.sendingDeletePlanningData = true;
            this.showDeletePlanningDataFailed = false;
            this.$refs.confirmDeletePlanningData?.hide();
            let self = this;
            axios.delete(route('auctions.delete_planned_auctions'))
                .then(() => {
                    self.cancelDeleteAuctionsDialog();
                    self.refreshAuctionsList();
                    self.sendingDeletePlanningData = false;
                })
                .catch((err) => {
                    self.showDeletePlanningDataFailed = true;
                    self.deletePlanningDataErrors = [this.$t('pages.auctions.planning.overview.delete_all_data.failed')];
                    self.sendingDeletePlanningData = false;
                    console.error(err);
                });
        },
        cancelDeleteAuctionsDialog() {
            this.$refs.deletePlanningData?.hide();
        },
        cancelConfirmDeleteAuctionsDialog() {
            this.$refs.confirmDeletePlanningData?.hide();
        },
        rawAuctionSource(auction) {
            return this.rawAuctions?.find(n => n.auction_date === auction.date_start.split(' ')[0])?.source || 1;
        },
        getRawAuction(auction) {
            return this.rawAuctions?.find(n => n.auction_date === auction.date_start.split(' ')[0]);
        },
        showDeleteDialog() {
            this.$refs.deletePlanningData?.show();
        },
        showConfirmDeletePlanningData() {
            this.$refs.confirmDeletePlanningData?.show();
        },
        deletePlanningDataFailedAlertClosed() {
            this.showDeletePlanningDataFailed = false;
            this.deletePlanningDataErrors = [];
        }
    },
    props: {
        auctions: Object,
        rawAuctions: [],
        activeAuction: Object
    },
    computed: {
        activeAuctionId() {
            if (this.activeAuction && this.activeAuction.id) {
                return this.activeAuction.id;
            }
            return null;
        }
    },
    data() {
        return {
            sendingDeletePlanningData: false,
            showDeletePlanningDataFailed: false,
            deletePlanningDataErrors: []
        };
    }
};
</script>
